import { ThemeProvider } from 'next-themes';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';

import { queryClientInstance } from '@/lib/query-client';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import ScrollToTop from '@/components/ScrollToTop';
import PageNotFound from '@/lib/PageNotFound';

import Home from '@/pages/Home';
import Leaderboard from '@/pages/Leaderboard';
import Badges from '@/pages/Badges';
import Team from '@/pages/Team';
import KbTests from '@/pages/KbTests';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';

// What changed from the Base44 version:
//   - No "public settings" probe. That was a Base44 API call with no equivalent.
//   - No UserNotRegisteredError. Unapproved signups are rejected at the
//     database, so a user who is not allowed in never gets an account at all.
//   - No navigateToLogin(). Routing is ours: ProtectedRoute redirects.
//   - Signed-in users are bounced away from the auth pages.

function AppRoutes() {
  const { loading, isAuthenticated } = useAuth();

  // One loading gate for the whole app while the session is restored.
  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Auth pages: only reachable when signed out */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
      />
      <Route
        path="/register"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Register />}
      />
      <Route
        path="/forgot-password"
        element={isAuthenticated ? <Navigate to="/" replace /> : <ForgotPassword />}
      />
      {/* Reset stays reachable while signed in: Supabase signs the user in via
          the recovery link before they choose a new password. */}
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Everything below requires a session */}
      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<Home />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/badges" element={<Badges />} />
        <Route path="/team" element={<Team />} />
        <Route path="/kb-tests" element={<KbTests />} />
      </Route>

      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <AuthProvider>
        <QueryClientProvider client={queryClientInstance}>
          <Router>
            <ScrollToTop />
            <AppRoutes />
          </Router>
          <Toaster richColors position="top-center" />
        </QueryClientProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}